import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Card, FormHelperText, Stack, Typography } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import { useCreateImporterEmail } from '@/service-library/hooks/importer-emails'
import { ImporterEmail } from '@/types/importer-emails'
import { Organization } from '@/types/organizations'
import { Project } from '@/types/projects'
import { getGlobalBaseUrl, getOrgSlug, localHostnames } from '@/utils/auth'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import {
  FormAutocomplete,
  FormTextField,
  PixyDocsForm,
} from '@/components/forms'
import OrganizationPickerWithDialog from '@/components/organization-select/OrganizationPickerWithDialog'

type AddImporterEmailDialogProps = {
  isFetchingOrgs: boolean
  listQueryKey: QueryKey
  organizations: Organization[]
  projects: Project[]
  overlay: OverlayState
  importerEmails: ImporterEmail[]
}

type FormValues = {
  org: Organization
  name: string
  project: Project | null
}

export const isRunningLocally = () => {
  const hostname = window.location.hostname
  return localHostnames.includes(hostname)
}

function getImportEmailDomain() {
  let baseUrl = window.location.host
  const orgSlug = getOrgSlug()
  if (orgSlug) {
    baseUrl = getGlobalBaseUrl(orgSlug, baseUrl)
  }
  if (isRunningLocally()) {
    return baseUrl
  }
  // We replace the word "app" with "import", except for the prs links (we replace
  // the whole prsX-app) since it uses dev for the backend
  const pattern = /\bprs\d+-app\b|\bapp\b/g
  return baseUrl.replace(pattern, 'import')
}

export default function AddImporterEmailDialog({
  isFetchingOrgs,
  listQueryKey,
  organizations,
  projects,
  overlay,
  importerEmails,
}: AddImporterEmailDialogProps) {
  const { rootOrganization } = useCurrentAccountContext()

  const { createImporterEmail } = useCreateImporterEmail({
    listQueryKey,
    onError: () => {
      showErrorSnackbar('Unable to create email. Please try again later.')
    },
  })

  // We want to check for the org-project combinations that already have an importer email address to prevent duplicates.
  const existingEmailsCombinedKeys = useMemo(() => {
    const disabledIds: string[] = []
    importerEmails.forEach((email) => {
      const comboWithProject = `${email.org_id}-${email.project_id || 'auto'}`
      disabledIds.push(comboWithProject)
    })
    return disabledIds
  }, [importerEmails])

  const defaultValues = useMemo(
    () => ({
      org: organizations[0],
      name: generateUuid(),
      project: null,
    }),
    [organizations],
  )

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
  })

  const {
    formState: { dirtyFields, errors, isValid },
    reset,
    setValue,
    trigger,
    watch,
  } = methods

  const handleSubmit = (values: FormValues) => {
    values.name = values.name.trim()
    const orgId = values.org.id
    const projectId = values.project?.id || null
    // We just use it for optimizing updates
    const placeholderEmail = `${values.name}@${getImportEmailDomain()}`

    createImporterEmail({
      id: generateUuid(),
      name: values.name,
      email: placeholderEmail,
      project_id: projectId,
      org_id: orgId,
    })
    overlay.close()
  }

  useEffect(() => {
    reset(defaultValues)
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    if (overlay.isOpen) {
      timeoutId = setTimeout(() => {
        trigger('project')
      }, 0)
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [defaultValues, overlay.isOpen, reset, trigger])

  const org = watch('org')

  return (
    <Dialog title="New Upload Email Address" {...overlay} maxWidth="sm">
      <PixyDocsForm methods={methods} onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={3}>
            <Card>
              <OrganizationPickerWithDialog
                currentOrganization={org}
                organizations={organizations}
                rootTreeOrgId={rootOrganization.id}
                isFetching={isFetchingOrgs}
                onSave={(organization) => {
                  setValue('org', organization, {
                    shouldDirty: true,
                  })
                  setTimeout(() => {
                    trigger('project')
                  }, 0)
                }}
              />
            </Card>

            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <FormTextField
                name="name"
                error={!!errors.name}
                label="Email"
                sx={{ width: 335 }}
                required
                requiredErrorText="Field cannot be empty"
                useControllerProps={{
                  rules: {
                    validate: (value) => {
                      if (!value.trim().length) return 'Field cannot be empty'
                      if (!/^[0-9A-Za-z.-]+$/.test(value)) {
                        return 'Only accepts numbers, letters, periods, and dashes.'
                      }
                    },
                  },
                }}
              />
              <Typography>@{getImportEmailDomain()}</Typography>
            </Stack>
          </Stack>
          {!dirtyFields.name && (
            <FormHelperText sx={{ ml: 1 }}>
              We generated this email address for you, but you can change it if
              you&apos;d like.
            </FormHelperText>
          )}
          {errors?.name && (
            <FormHelperText error sx={{ ml: 1 }}>
              {errors.name.message}
            </FormHelperText>
          )}

          <FormAutocomplete
            sx={{ mt: 3 }}
            name="project"
            label="Project"
            options={projects}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id}
            helperText="If a project is selected, uploaded files will be assigned to that project. If a project is not selected, they will be sent to Pixydocs AI for automatic assignment."
            useControllerProps={{
              rules: {
                validate: (value) => {
                  if (
                    existingEmailsCombinedKeys.includes(
                      `${org.id}-${value?.id || 'auto'}`,
                    )
                  ) {
                    return 'An upload email address for this organization and project combination already exists.'
                  }
                  return true
                },
              },
            }}
            textFieldProps={{
              slotProps: {
                inputLabel: { shrink: true },
              },
            }}
          />
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit" disabled={!isValid}>
            Save
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
